/**
 * Translation between the canonical `{module}.{entity}` key and legacy spellings.
 *
 * `{module}.{entity}` is the canonical entity-reference key space
 * (`docs/platform/entity-relationship-surfaces-design.md` § 1). Five other spellings
 * still exist in the tree, so anything that hands an entity reference across a
 * subsystem boundary has to translate. This module is that translation — the only
 * one. Data comes from `apps/frontend/src/generated/entity-vocabulary.ts`, generated
 * from the master entity catalogue; nothing here re-derives it. The matching Python
 * layer is `shared/entity_vocabulary.py`.
 *
 * Every lookup FAILS CLOSED. A silently-guessed entity reference reads the wrong
 * entity, and that does not announce itself — so the `to*`/`from*` helpers throw
 * `UnknownEntityReferenceError` on an unknown key rather than passing the input
 * through. Callers that legitimately expect misses use the `try*` variants, which
 * return `null`.
 *
 * This is NOT the URL registry: `entityUrls.ts` answers "where does this entity
 * live in the app", this answers "what is this entity called over there".
 */
import {
  COMMENT_ENTITY_TYPE_TO_ENTITY_TYPE,
  ENTITY_VOCABULARY,
  FOLLOW_ENTITY_TYPE_TO_ENTITY_TYPE,
  OBJECT_TYPE_TO_ENTITY_TYPE,
  SHARING_OBJECT_TYPE_TO_ENTITY_TYPE,
  TABLE_TO_ENTITY_TYPE,
  type EntityVocabularyEntry,
} from '../../generated/entity-vocabulary';

/** A legacy entity-reference key space. */
export enum LegacySpace {
  /** Physical table name. Also the prefix of a `table:uuid` entity ref. */
  Table = 'table',
  /** `shared/versioning` object_type — the table name unless overridden. */
  ObjectType = 'objectType',
  /** `shared/config/sharing_config` object type. */
  SharingObjectType = 'sharingObjectType',
  /** Bare noun in `entity_follows.entity_type`. */
  FollowEntityType = 'followEntityType',
  /**
   * Bare noun in `entity_comments.entity_type`. NOT the follow space — the two
   * disagree today (follows says `company` where comments says `organization`).
   */
  CommentEntityType = 'commentEntityType',
}

const REVERSE_INDEXES: Record<LegacySpace, Record<string, string>> = {
  [LegacySpace.Table]: TABLE_TO_ENTITY_TYPE,
  [LegacySpace.ObjectType]: OBJECT_TYPE_TO_ENTITY_TYPE,
  [LegacySpace.SharingObjectType]: SHARING_OBJECT_TYPE_TO_ENTITY_TYPE,
  [LegacySpace.FollowEntityType]: FOLLOW_ENTITY_TYPE_TO_ENTITY_TYPE,
  [LegacySpace.CommentEntityType]: COMMENT_ENTITY_TYPE_TO_ENTITY_TYPE,
};

const ENTITY_REF_SEPARATOR = ':';

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** An entity reference does not resolve in the key space it was read in. */
export class UnknownEntityReferenceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnknownEntityReferenceError';
  }
}

/** Every canonical `{module}.{entity}` key, sorted. */
export const ENTITY_TYPES: readonly string[] = Object.keys(ENTITY_VOCABULARY);

/** True if `entityType` is a canonical `{module}.{entity}` key. */
export function isEntityType(entityType: string): boolean {
  return Object.prototype.hasOwnProperty.call(ENTITY_VOCABULARY, entityType);
}

function requireEntry(entityType: string): EntityVocabularyEntry {
  const entry = isEntityType(entityType) ? ENTITY_VOCABULARY[entityType] : undefined;
  if (!entry) {
    throw new UnknownEntityReferenceError(`"${entityType}" is not a known entity type`);
  }
  return entry;
}

/** The owning module's id (the module-availability key). Throws if unknown. */
export function getModuleId(entityType: string): string {
  return requireEntry(entityType).moduleId;
}

/** Spell a canonical entity type in `space`, or null if it has no spelling there. */
export function tryToLegacy(entityType: string, space: LegacySpace): string | null {
  if (!isEntityType(entityType)) return null;
  return ENTITY_VOCABULARY[entityType][space];
}

/** Spell a canonical entity type in `space`. Throws if it has no spelling there. */
export function toLegacy(entityType: string, space: LegacySpace): string {
  requireEntry(entityType);
  const spelling = tryToLegacy(entityType, space);
  if (spelling === null) {
    throw new UnknownEntityReferenceError(
      `entity type "${entityType}" has no ${space} spelling`,
    );
  }
  return spelling;
}

/** Resolve a legacy spelling to its canonical entity type, or null. */
export function tryFromLegacy(value: string, space: LegacySpace): string | null {
  const index = REVERSE_INDEXES[space];
  return Object.prototype.hasOwnProperty.call(index, value) ? index[value] : null;
}

/** Resolve a legacy spelling to its canonical entity type. Throws if unknown. */
export function fromLegacy(value: string, space: LegacySpace): string {
  const entityType = tryFromLegacy(value, space);
  if (entityType === null) {
    throw new UnknownEntityReferenceError(`"${value}" is not a known ${space}`);
  }
  return entityType;
}

/** Build the `table:uuid` entity ref for a canonical entity type. */
export function buildEntityRef(entityType: string, entityId: string): string {
  return `${toLegacy(entityType, LegacySpace.Table)}${ENTITY_REF_SEPARATOR}${entityId}`;
}

/**
 * Parse a `table:uuid` entity ref into `[entityType, id]`, or null.
 *
 * null covers all three ways a ref can be unusable — malformed, an unknown table,
 * or a non-UUID id — because to a caller they are the same miss.
 */
export function tryParseEntityRef(ref: string): [string, string] | null {
  const separatorIndex = ref.indexOf(ENTITY_REF_SEPARATOR);
  if (separatorIndex === -1) return null;
  const table = ref.slice(0, separatorIndex);
  const id = ref.slice(separatorIndex + 1);
  const entityType = tryFromLegacy(table, LegacySpace.Table);
  if (entityType === null) return null;
  if (!UUID_PATTERN.test(id)) return null;
  return [entityType, id];
}

/** Parse a `table:uuid` entity ref into `[entityType, id]`. Throws if unusable. */
export function parseEntityRef(ref: string): [string, string] {
  const parsed = tryParseEntityRef(ref);
  if (parsed === null) {
    throw new UnknownEntityReferenceError(`"${ref}" is not a resolvable entity ref`);
  }
  return parsed;
}
